import type { CadDocument } from '../model/document';
import type { BlockRefEntity, CadEntity } from '../model/entity';
import { polylineLength } from '../model/geom';
import {
  footprintSquareMeters,
  normalizeParams,
  shelfLengthMeters,
} from '../blocks/parametric';
import { readParams, variantCode, type CatalogBlock } from '../blocks/catalog';
import { diffDocuments, type DocumentDiff } from './documentDiff';

/**
 * 리뉴얼 물량 산출 (작업지시서 P5-05, P4-09 위에 구축)
 *
 * ★ 이것이 이 제품의 실무 효과가 가장 큰 기능이다 (`고도화_제안_v2.md` 5.1-①).
 *   "현행 → A안" 을 고르면 철거/신설/이설 수량과 추정 비용이 바로 나온다.
 *   지금은 사람이 도면 두 장을 놓고 세다가 틀린다.
 *
 * ── 비용 산정에서 반드시 지켜야 할 것 ───────────────────────────────────
 *
 *   **이설(relocate)을 철거+신설로 세면 견적이 두 배가 된다.**
 *   곤돌라를 2m 옮긴 것은 이미 있는 물건을 옮긴 것이지 새로 사는 게 아니다.
 *   이설 단가는 설치비만 잡고 자재비는 잡지 않는다.
 */

/** 이설 시 자재비는 들지 않는다. 설치비만 계산한다. */
export interface CostModel {
  /** 이설 인건비를 설치비의 몇 배로 볼 것인가. 실무 관행상 0.6~0.8 */
  relocateFactor: number;
  /** 철거 비용을 설치비의 몇 배로 볼 것인가 */
  demolishFactor: number;
}

export const DEFAULT_COST_MODEL: CostModel = {
  relocateFactor: 0.7,
  demolishFactor: 0.4,
};

export interface QuantityRow {
  code: string;
  name: string;
  categoryCode: string;

  beforeCount: number;
  afterCount: number;

  /** 철거 — 이전 도면에만 있음 */
  demolish: number;
  /** 신설 — 새 도면에만 있음 */
  install: number;
  /** 이설 — 양쪽에 있고 위치가 바뀜 */
  relocate: number;
  /** 유지 — 그대로 */
  keep: number;

  /** 진열 선반 연장 변화 (m) */
  shelfDeltaM: number;
  /** 점유 면적 변화 (㎡) */
  footprintDeltaM2: number;

  /** 추정 비용 (원) */
  costKrw: number;
  costBreakdown: { install: number; relocate: number; demolish: number };
}

export interface QuantityTotals {
  demolish: number;
  install: number;
  relocate: number;
  keep: number;

  shelfBeforeM: number;
  shelfAfterM: number;
  shelfDeltaM: number;
  /** 진열 연장 증감률 (%) */
  shelfDeltaPercent: number;

  footprintBeforeM2: number;
  footprintAfterM2: number;

  /** 벽체 총 연장 변화 (m) — 구조 변경 규모를 가늠한다 */
  wallDeltaM: number;

  costKrw: number;
}

export interface QuantityDiff {
  rows: QuantityRow[];
  totals: QuantityTotals;
  /** 카탈로그에 없어 집계하지 못한 블록 코드 */
  unknownBlocks: string[];
  diff: DocumentDiff;
}

/* -------------------------------------------------------------------------- *
 * 산출
 * -------------------------------------------------------------------------- */

export function diffQuantities(
  before: CadDocument,
  after: CadDocument,
  catalog: Map<string, CatalogBlock>,
  cost: CostModel = DEFAULT_COST_MODEL
): QuantityDiff {
  const diff = diffDocuments(before, after);
  const rows = new Map<string, QuantityRow>();
  const unknown = new Set<string>();

  const bump = (
    e: CadEntity,
    field: 'demolish' | 'install' | 'relocate' | 'keep'
  ): void => {
    if (e.type !== 'blockref') return;

    const baseCode = e.blockCode.split('@')[0]!;
    const block = catalog.get(baseCode);
    if (!block) {
      unknown.add(baseCode);
      return;
    }

    const params = readParams(e);
    const key = block.spec ? variantCode(baseCode, normalizeParams(block.spec, params)) : baseCode;

    let row = rows.get(key);
    if (!row) {
      row = {
        code: key,
        name: block.name + (block.spec ? ` ${describe(block, params)}` : ''),
        categoryCode: block.categoryCode,
        beforeCount: 0,
        afterCount: 0,
        demolish: 0,
        install: 0,
        relocate: 0,
        keep: 0,
        shelfDeltaM: 0,
        footprintDeltaM2: 0,
        costKrw: 0,
        costBreakdown: { install: 0, relocate: 0, demolish: 0 },
      };
      rows.set(key, row);
    }

    row[field] += 1;
    if (field === 'demolish') row.beforeCount += 1;
    else if (field === 'install') row.afterCount += 1;
    else {
      // 이설·유지는 양쪽에 존재한다
      row.beforeCount += 1;
      row.afterCount += 1;
    }
  };

  for (const e of diff.removed) bump(e, 'demolish');
  for (const e of diff.added) bump(e, 'install');
  for (const m of diff.moved) bump(m.after, 'relocate');
  for (const e of diff.unchanged) bump(e, 'keep');

  // 변경(modified)은 규격이 바뀐 경우가 대부분이다.
  // 이전 규격은 철거, 새 규격은 신설로 나눠 센다 — 실제로 물건이 바뀌기 때문이다.
  for (const m of diff.modified) {
    if (m.before.type === 'blockref') bump(m.before, 'demolish');
    if (m.after.type === 'blockref') bump(m.after, 'install');
  }

  /* ---- 행별 지표와 비용 ---- */
  for (const row of rows.values()) {
    const baseCode = row.code.split('@')[0]!;
    const block = catalog.get(baseCode);
    if (!block) continue;

    const params = paramsFromVariant(row.code);
    const perUnitShelf = block.spec ? shelfLengthMeters(block.spec, params) : 0;
    const perUnitArea = block.spec ? footprintSquareMeters(block.spec, params) : 0;

    row.shelfDeltaM = round2((row.afterCount - row.beforeCount) * perUnitShelf);
    row.footprintDeltaM2 = round2((row.afterCount - row.beforeCount) * perUnitArea);

    const material = block.cost?.standardKrw ?? 0;
    const install = block.cost?.installKrw ?? 0;

    row.costBreakdown = {
      install: row.install * (material + install),
      // 이설은 자재비를 잡지 않는다 — 이미 있는 물건을 옮기는 것이다
      relocate: Math.round(row.relocate * install * cost.relocateFactor),
      demolish: Math.round(row.demolish * install * cost.demolishFactor),
    };
    row.costKrw =
      row.costBreakdown.install + row.costBreakdown.relocate + row.costBreakdown.demolish;
  }

  /* ---- 합계 ---- */
  const list = [...rows.values()].sort(
    (a, b) => b.install + b.demolish + b.relocate - (a.install + a.demolish + a.relocate)
  );

  const shelfBefore = totalShelf(before.entities, catalog);
  const shelfAfter = totalShelf(after.entities, catalog);

  const totals: QuantityTotals = {
    demolish: sum(list, (r) => r.demolish),
    install: sum(list, (r) => r.install),
    relocate: sum(list, (r) => r.relocate),
    keep: sum(list, (r) => r.keep),

    shelfBeforeM: round2(shelfBefore),
    shelfAfterM: round2(shelfAfter),
    shelfDeltaM: round2(shelfAfter - shelfBefore),
    shelfDeltaPercent:
      shelfBefore > 0 ? round2(((shelfAfter - shelfBefore) / shelfBefore) * 100) : 0,

    footprintBeforeM2: round2(totalFootprint(before.entities, catalog)),
    footprintAfterM2: round2(totalFootprint(after.entities, catalog)),

    wallDeltaM: round2(wallLengthM(after.entities) - wallLengthM(before.entities)),

    costKrw: sum(list, (r) => r.costKrw),
  };

  return { rows: list, totals, unknownBlocks: [...unknown], diff };
}

/* -------------------------------------------------------------------------- *
 * 보조
 * -------------------------------------------------------------------------- */

function describe(block: CatalogBlock, params: Record<string, number>): string {
  if (!block.spec) return '';
  const p = normalizeParams(block.spec, params);
  const bits: string[] = [];
  if (p.width) bits.push(String(p.width));
  if (p.depth) bits.push(`×${p.depth}`);
  if (p.bays && p.bays > 1) bits.push(`${p.bays}베이`);
  if (p.shelfLevels) bits.push(`${p.shelfLevels}단`);
  return bits.join(' ');
}

/**
 * 변형 코드에서 파라미터를 복원한다.
 * `GONDOLA@b1-d900-s5-w900` → { bays:1, depth:900, shelfLevels:5, width:900 }
 *
 * variantCode 가 키의 **첫 글자**만 쓰므로 되돌리려면 매핑이 필요하다.
 */
const KEY_BY_INITIAL: Record<string, string> = {
  w: 'width',
  d: 'depth',
  b: 'bays',
  s: 'shelfLevels',
};

export function paramsFromVariant(code: string): Record<string, number> {
  const at = code.indexOf('@');
  if (at < 0) return {};
  const out: Record<string, number> = {};
  for (const part of code.slice(at + 1).split('-')) {
    const initial = part[0];
    const value = Number(part.slice(1));
    if (!initial || !Number.isFinite(value)) continue;
    const key = KEY_BY_INITIAL[initial];
    if (key) out[key] = value;
  }
  return out;
}

function totalShelf(entities: CadEntity[], catalog: Map<string, CatalogBlock>): number {
  let total = 0;
  for (const e of entities) {
    if (e.type !== 'blockref') continue;
    const block = catalog.get(e.blockCode.split('@')[0]!);
    if (!block?.spec) continue;
    total += shelfLengthMeters(block.spec, readParams(e as BlockRefEntity));
  }
  return total;
}

function totalFootprint(entities: CadEntity[], catalog: Map<string, CatalogBlock>): number {
  let total = 0;
  for (const e of entities) {
    if (e.type !== 'blockref') continue;
    const block = catalog.get(e.blockCode.split('@')[0]!);
    if (!block?.spec) continue;
    total += footprintSquareMeters(block.spec, readParams(e as BlockRefEntity));
  }
  return total;
}

/** 벽체 레이어의 총 연장 (m) — 구조 변경 규모 */
function wallLengthM(entities: CadEntity[]): number {
  let um = 0;
  for (const e of entities) {
    if (e.layer !== 'A-WALL') continue;
    if (e.type === 'line') {
      um += Math.hypot(e.b.x - e.a.x, e.b.y - e.a.y);
    } else if (e.type === 'polyline') {
      um += polylineLength(e.points, e.closed);
    }
  }
  return um / 1_000_000;
}

function sum<T>(list: T[], f: (t: T) => number): number {
  return list.reduce((acc, t) => acc + f(t), 0);
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

/* -------------------------------------------------------------------------- *
 * 내보내기용 표
 * -------------------------------------------------------------------------- */

/** 엑셀로 붙여넣을 수 있는 TSV. 실무자가 그대로 견적서에 옮긴다. */
export function quantityToTsv(q: QuantityDiff): string {
  const head = [
    '분류', '품목', '현행', '변경후', '철거', '신설', '이설', '유지',
    '진열연장증감(m)', '점유면적증감(㎡)', '추정비용(원)',
  ].join('\t');

  const rows = q.rows.map((r) =>
    [
      r.categoryCode,
      r.name,
      r.beforeCount,
      r.afterCount,
      r.demolish,
      r.install,
      r.relocate,
      r.keep,
      r.shelfDeltaM,
      r.footprintDeltaM2,
      r.costKrw,
    ].join('\t')
  );

  const t = q.totals;
  const total = [
    '합계', '',
    '', '',
    t.demolish, t.install, t.relocate, t.keep,
    t.shelfDeltaM, round2(t.footprintAfterM2 - t.footprintBeforeM2), t.costKrw,
  ].join('\t');

  return [head, ...rows, total].join('\n');
}
